/**
 * Shared zod schemas (PRD §12): the same shapes validate the client form, the API body and
 * the service layer. They cover structure and bounds; the contextual rules of PRD §6.4 (caps,
 * assignment windows, cross-project budget) need the database and live in the validation service.
 */

import { z } from "zod";
import { isBusinessDate } from "../dates";
import type { SessionRole } from "./auth";

export const businessDateSchema = z
  .string()
  .refine(isBusinessDate, { message: "Expected a YYYY-MM-DD business date" });

export const uuidSchema = z.string().uuid();
/** Shared by G-Admin `/admin/projects/:id/reminders/send` and CA `/projects/:id/reminders/send`. */
export const reminderSendSchema = z.object({
  userIds: z.array(uuidSchema).optional(),
  locale: z.enum(["de", "en"]).optional(),
});
export const inviteCopyLinkSchema = z.object({
  userId: uuidSchema,
});
export const userRoleSchema = z.enum(["grantonomy_admin", "company_admin", "employee"]);
export const entryChannelSchema = z.enum(["employee", "admin", "import", "system", "ops"]);
export const absenceTypeSchema = z.enum(["vacation", "sick", "other"]);
export const absenceFractionSchema = z.union([z.literal(0), z.literal(0.5), z.literal(1)]);

/** A change reason only counts once it says something (PRD §6.4 D5/T5). */
export const changeReasonSchema = z.string().trim().min(3);

export const dayRecordInputSchema = z
  .object({
    userId: uuidSchema,
    entryDate: businessDateSchema,
    totalHours: z.number().nonnegative().max(24),
    absenceType: absenceTypeSchema.nullable().default(null),
    absenceFraction: absenceFractionSchema.default(0),
    enteredVia: entryChannelSchema,
    lastChangeReason: changeReasonSchema.nullable().default(null),
  })
  .refine((value) => (value.absenceType === null) === (value.absenceFraction === 0), {
    message: "An absence needs both a type and a fraction",
    path: ["absenceFraction"],
  });

export const timeEntryInputSchema = z.object({
  userId: uuidSchema,
  projectId: uuidSchema,
  entryDate: businessDateSchema,
  rdHours: z.number().nonnegative().max(24),
  enteredVia: entryChannelSchema,
  lastChangeReason: changeReasonSchema.nullable().default(null),
});

export const weekNoteInputSchema = z.object({
  userId: uuidSchema,
  projectId: uuidSchema,
  year: z.number().int().min(2000).max(2100),
  isoWeek: z.number().int().min(1).max(53),
  note: z.string().default(""),
});

export const monthLockInputSchema = z
  .object({
    projectId: uuidSchema,
    userId: uuidSchema.nullable().default(null),
    year: z.number().int().min(2000).max(2100),
    fromMonth: z.number().int().min(1).max(12),
    toMonth: z.number().int().min(1).max(12),
  })
  .refine((value) => value.fromMonth <= value.toMonth, {
    message: "fromMonth must be ≤ toMonth",
    path: ["toMonth"],
  });

export const assignmentInputSchema = z.object({
  userId: uuidSchema,
  projectId: uuidSchema,
  entryDate: businessDateSchema,
  rdShare: z.number().min(0).max(1),
  leaveDate: businessDateSchema.optional(),
});

// --------------------------------------------------------------------- Phase 4 wire shapes

/** One day inside a week-save body (PLAN.md D-32). Actor/user/project come from the route. */
export const weekDaySaveSchema = z
  .object({
    entryDate: businessDateSchema,
    totalHours: z.number().nonnegative().max(24),
    rdHours: z.number().nonnegative().max(24),
    absenceType: absenceTypeSchema.nullable().default(null),
    absenceFraction: absenceFractionSchema.default(0),
    /**
     * Ticket 116: opt-in to delete other projects' time_entries on this date before
     * stamping full-day absence. Omitted/false keeps C5 (`ABSENCE_BLOCKED_BY_OTHER_PROJECT_HOURS`).
     */
    clearOtherProjectHours: z.boolean().optional(),
  })
  .refine((value) => (value.absenceType === null) === (value.absenceFraction === 0), {
    message: "An absence needs both a type and a fraction",
    path: ["absenceFraction"],
  });

export const weekSaveSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  isoWeek: z.number().int().min(1).max(53),
  days: z.array(weekDaySaveSchema).max(7),
  note: z.string().max(4000).default(""),
  /**
   * Optional on the wire; `saveWeek` requires ≥3 trimmed chars when any submitted day would
   * modify an existing day_records / time_entries row (anySaved week — not the old >7-day rule).
   */
  changeReason: changeReasonSchema.nullable().optional().default(null),
});

export const monthLockCreateSchema = z
  .object({
    year: z.number().int().min(2000).max(2100),
    fromMonth: z.number().int().min(1).max(12),
    toMonth: z.number().int().min(1).max(12),
  })
  .refine((value) => value.fromMonth <= value.toMonth, {
    message: "fromMonth must be ≤ toMonth",
    path: ["toMonth"],
  });

export const monthUnlockSchema = z.object({
  reason: changeReasonSchema,
});

/** What the calendar GET returns for one day of one project. */
export interface CalendarDay {
  entryDate: string;
  totalHours: number | null;
  rdHours: number | null;
  /** R&D share for F&E (Antrag): snapshot when saved, else history-resolved. */
  antragRdShare: number;
  absenceType: AbsenceType | null;
  absenceFraction: number;
  /**
   * Project that recorded a shared absence when it is not the calendar's current project.
   * Null when unknown (legacy), cleared, or the source is the project being viewed.
   */
  absenceSourceProjectId: string | null;
  absenceSourceProjectName: string | null;
  holiday: string | null;
  isWorkday: boolean;
  isRetroactive: boolean;
  saved: boolean;
  /** Admin-edit trail for the year-matrix export (PRD §9); null when untouched. */
  adminName: string | null;
  adminEmail: string | null;
  adminEditedAt: string | null;
  weekNote: string | null;
  /**
   * Other projects that already have a time_entry for this person/date.
   * Empty ⇒ no Stunden ⓘ. Non-empty even when this project also has an entry
   * (Gesamt is still shared).
   */
  hoursSourceProjects: { id: string; name: string }[];
}

export interface CalendarWeekNote {
  year: number;
  isoWeek: number;
  note: string;
}

export interface CalendarMonth {
  userId: string;
  projectId: string;
  projectName: string;
  companyName: string;
  bundesland: string;
  rdShare: number;
  weeklyHours: number;
  workdaysPerWeek: number;
  workWeekdays: number[];
  year: number;
  month: number;
  dailyCap: number;
  defaultDay: number;
  /** Inclusive project start — client derives the tracking window with assignmentEntryDate. */
  projectStartDate: string;
  /** Inclusive project end. */
  projectEndDate: string;
  /** Inclusive assignment entry date for this person on this project. */
  assignmentEntryDate: string;
  /** Inclusive last trackable day on this project; null = open until project end. */
  assignmentLeaveDate: string | null;
  /**
   * Active (non-archived) project assignments for this person.
   * Kept on the payload; Stunden ⓘ uses hoursSourceProjects per day, not this count.
   */
  activeAssignmentCount: number;
  days: CalendarDay[];
  weekNotes: CalendarWeekNote[];
  lockedMonths: {
    year: number;
    month: number;
    lockId: string;
    /** Project that holds the active lock (may differ from the calendar project — §7.2). */
    projectId: string;
    projectName: string;
  }[];
}

export interface MonthLockSummary {
  id: string;
  projectId: string;
  year: number;
  /** Inclusive start month (1–12) of the locked interval. */
  fromMonth: number;
  /** Inclusive end month (1–12) of the locked interval. */
  toMonth: number;
  lockedAt: string;
  lockedBy: string;
  /** Null while the lock is active; set after G-Admin unlock. */
  unlockedAt: string | null;
  unlockReason: string | null;
}

export interface LiveEmployeeMonth {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Optional job title. */
  position: string | null;
  /** Person weekly hours (capacity); used by dashboard live-status week KPIs. */
  weeklyHours: number;
  rdShare: number;
  months: {
    month: number;
    totalHours: number;
    rdHours: number;
    /** Planned R&D hours for the month (capacity × rd_share); not stored. */
    plannedHours: number;
    weeksSaved: number;
    /**
     * Countable weeks for Übersicht display (in-month / window / not-after filters).
     * Not a tracking-window predicate — use `inTrackingWindow` for that.
     */
    weeksTotal: number;
    /**
     * True when the calendar month overlaps the assignee tracking window
     * (`monthOverlapsWindow`). Independent of how many weeks are countable so far.
     */
    inTrackingWindow: boolean;
    completionPct: number;
    /** ISO week numbers that count in this month (`monthStatsForEmployee` filters). */
    weekNumbers: number[];
  }[];
}

export interface LiveDayEntry {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  entryDate: string;
  totalHours: number;
  rdHours: number;
  absenceType: AbsenceType | null;
  absenceFraction: number;
  /** Holiday name for the day when applicable (export). */
  holiday: string | null;
  /** Week note for the ISO week when present (export). */
  note: string | null;
  /** Admin-edit trail for the year-matrix export; null when untouched. */
  adminName: string | null;
  adminEmail: string | null;
  /** Last-editor role for year-matrix Admin Role; null when untouched. */
  adminRole: SessionRole | null;
  adminEditedAt: string | null;
}

/** Project week description for the year-matrix "Week Notes (Descriptions)" sheet. */
export interface LiveWeekNote {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  year: number;
  isoWeek: number;
  note: string;
  updatedAt: string;
}

export interface LiveOverview {
  projectId: string;
  projectName: string;
  companyId: string;
  companyName: string;
  bundesland: string;
  year: number;
  locks: MonthLockSummary[];
  employees: LiveEmployeeMonth[];
  /** Day rows for the English "All Entries" export sheet (year-matrix XLSX). */
  dayEntries: LiveDayEntry[];
  /** Week descriptions for the "Week Notes (Descriptions)" export sheet (may be empty). */
  weekNotes: LiveWeekNote[];
  /** Count of week attachment files for the selected year (ZIP export guard). */
  attachmentCount: number;
}

/** One month cell in the dashboard live-status year matrix (reference YearMatrix). */
export interface LiveStatusMonthCell {
  month: number;
  plannedHours: number;
  actualHours: number;
  /** Gesamt hours for the month (tooltip + rdPct). */
  totalHours: number;
  completionPct: number;
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
  weeksSaved: number;
  weeksTotal: number;
  inTrackingWindow: boolean;
  /** ISO weeks that count in this month (tooltip `KW a–b`). */
  weekNumbers: number[];
  weeksWithNote: number;
  lastEntryDate: string | null;
  /** R&D hours / total hours for the month (`rdSharePct`, 0 when empty). */
  rdPct: number;
}

export interface LiveStatusEmployee {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  hasWeekNote: boolean;
  lastEntryDate: string | null;
  months: LiveStatusMonthCell[];
  yearCompletionPct: number;
  /** Current Berlin-month weeks with a complete tracking week (`notAfter: today`). */
  weeksSaved: number;
  /** Current Berlin-month countable elapsed weeks (`notAfter: today`). */
  weeksTotal: number;
  /** Sum of in-window, non-future month weeks in the live-status year. */
  yearWeeksSaved: number;
  yearWeeksTotal: number;
  /** Incomplete months (1–12) in the live-status year (red and yellow). */
  missingMonths: number[];
}

export interface LiveStatusProject {
  projectId: string;
  projectName: string;
  companyName: string;
  /** Project master-data start date (`YYYY-MM-DD`); shown under the card title. */
  startDate: string;
  year: number;
  month: number;
  employeesTotal: number;
  employeesEntered: number;
  /** Current-month completion % (0–100), ranked ascending on the dashboard. */
  completionPct: number;
  /** Sum of in-window employees' current-month `weeksSaved`. Idle projects: 0. */
  weeksSaved: number;
  /** Sum of in-window employees' current-month `weeksTotal`. Idle projects: 0. */
  weeksTotal: number;
  yearCompletionPct: number;
  plannedWeekHours: number;
  actualWeekHours: number;
  plannedMonthHours: number;
  actualMonthHours: number;
  withNoteThisWeek: number;
  /** Current-month employees with weeksSaved >= weeksTotal and weeksTotal > 0. */
  employeesComplete: number;
  /** Current-month employees with weeksTotal > 0 and weeksSaved === 0. */
  employeesWithoutEntry: number;
  /** Current-month R&D share (`rdSharePct` of summed hours). */
  monthRdPct: number;
  lastEntryDate: string | null;
  /** Berlin calendar days since `lastEntryDate` (0 = today). */
  daysSinceLastEntry: number | null;
  yearMonths: LiveStatusMonthCell[];
  employees: LiveStatusEmployee[];
}

export interface LiveStatus {
  activeProjects: number;
  employees: number;
  monthEntered: number;
  avgCompletionPct: number;
  /** Non-archived people in scope (company users); dashboard portfolio KPI. */
  peopleCount: number;
  /**
   * Distinct (user, project) pairs with tracked hours — proxy for exportable
   * Stundenzettel (v1 does not persist generated sheets).
   */
  exportableTimesheets: number;
  projects: LiveStatusProject[];
}

export type DayRecordInput = z.infer<typeof dayRecordInputSchema>;
export type TimeEntryInput = z.infer<typeof timeEntryInputSchema>;
export type WeekNoteInput = z.infer<typeof weekNoteInputSchema>;
export type MonthLockInput = z.infer<typeof monthLockInputSchema>;
export type AssignmentInput = z.infer<typeof assignmentInputSchema>;
export type WeekDaySaveInput = z.infer<typeof weekDaySaveSchema>;
export type WeekSaveInput = z.infer<typeof weekSaveSchema>;
export type MonthLockCreateInput = z.infer<typeof monthLockCreateSchema>;
export type MonthUnlockInput = z.infer<typeof monthUnlockSchema>;
export type UserRole = z.infer<typeof userRoleSchema>;
export type EntryChannel = z.infer<typeof entryChannelSchema>;
export type AbsenceType = z.infer<typeof absenceTypeSchema>;
